// Iter 418.t (Dirk, 2026-02-28) — Android-only camera+gallery chooser.
//
// Problem: on Android a plain `<input type="file" accept="image/*">`
// only shows the gallery — no camera option. iOS is fine, its native
// picker already offers "Take Photo / Photo Library / Choose File".
//
// Fix: this button ONLY intercepts on Android. On any other platform
// it renders as a normal file-input label so tapping it behaves
// EXACTLY like before (native iOS action sheet). On Android tapping
// pops a small in-app menu with two choices — "Take Photo" and
// "Choose from Gallery" — each wired to a hidden input (the camera
// one carries `capture="environment"`).
//
// Usage (drop-in replacement for a plain label+input pattern):
//   <PhotoPickerButton onFiles={(files) => …} multiple>
//     <CameraIcon /> Photo
//   </PhotoPickerButton>

import { useRef, useState, useEffect } from "react";
import { Camera as CameraIcon, ImageIcon } from "lucide-react";

const isAndroid = () =>
  typeof navigator !== "undefined" &&
  /Android/i.test(navigator.userAgent || "");

export default function PhotoPickerButton({
  onFiles,
  multiple = true,
  accept = "image/*",
  className = "",
  disabled = false,
  children,
  testId,
  title,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const camRef = useRef(null);
  const libRef = useRef(null);
  const nativeRef = useRef(null);
  const wrapRef = useRef(null);
  const android = isAndroid();

  // Close on outside click / Escape (Android menu only).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    onFiles?.(fileList);
    setOpen(false);
  };

  // iOS / desktop / anything-not-Android — render EXACTLY the same
  // label+input the caller had before, so the native picker shows and
  // the UX doesn't change.
  if (!android) {
    return (
      <label
        className={className}
        title={title}
        data-testid={testId}
        aria-label={ariaLabel || title || "Add photo"}
      >
        {children}
        <input
          ref={nativeRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          disabled={disabled}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </label>
    );
  }

  // Android — intercept the tap and show our own two-choice menu.
  const openMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setOpen((v) => !v);
  };

  return (
    <span ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={openMenu}
        disabled={disabled}
        className={className}
        data-testid={testId}
        title={title}
        aria-label={ariaLabel || title || "Add photo"}
      >
        {children}
      </button>
      {open && (
        // Iter 418.bg (Dirk, 2026-02-28) — full-screen bottom sheet on
        // Android. Previous absolute-positioned popup was getting
        // clipped by overflow-hidden ancestors (perf-capture rows sit
        // inside a scrollable card). fixed inset-0 escapes every
        // stacking context; the sheet is anchored to the bottom edge
        // with iOS-style rounded top corners + safe-area padding so
        // it never sits under the OS gesture bar.
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/60"
            onClick={() => setOpen(false)}
            data-testid={testId ? `${testId}-backdrop` : "photo-picker-backdrop"}
            aria-hidden="true"
          />
          <div
            className="fixed inset-x-0 bottom-0 z-[9999] bg-[#151821] border-t border-white/10 rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)]"
            data-testid={testId ? `${testId}-menu` : "photo-picker-menu"}
            role="menu"
          >
            <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mt-2 mb-1" aria-hidden="true" />
            <div className="px-4 pt-2 pb-1 text-xs uppercase tracking-wider text-[#a0aabf]">Add photo</div>
            <button
              type="button"
              onClick={() => camRef.current?.click()}
              className="flex items-center gap-3 w-full text-left px-5 py-4 text-slate-100 hover:bg-white/5 active:bg-white/10 border-t border-white/5"
              data-testid={testId ? `${testId}-camera` : undefined}
            >
              <CameraIcon className="w-5 h-5 shrink-0 text-emerald-300" />
              <span className="text-base">Take photo</span>
            </button>
            <button
              type="button"
              onClick={() => libRef.current?.click()}
              className="flex items-center gap-3 w-full text-left px-5 py-4 text-slate-100 hover:bg-white/5 active:bg-white/10 border-t border-white/5"
              data-testid={testId ? `${testId}-gallery` : undefined}
            >
              <ImageIcon className="w-5 h-5 shrink-0 text-cyan-300" />
              <span className="text-base">Choose from gallery</span>
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center w-full text-center px-5 py-3.5 text-[#a0aabf] hover:bg-white/5 border-t border-white/10 text-sm"
              data-testid={testId ? `${testId}-cancel` : undefined}
            >
              Cancel
            </button>
          </div>
        </>
      )}
      {/* Camera-only input — Android shells out to the rear camera. */}
      <input
        ref={camRef}
        type="file"
        accept={accept}
        capture="environment"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {/* Gallery / files input — no capture attribute. */}
      <input
        ref={libRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </span>
  );
}
